import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, MapPin, Locate, ExternalLink } from "lucide-react";

const PRESETS = [
  { name: "San Francisco",  lat: 37.7749,  lon: -122.4194 },
  { name: "New York",       lat: 40.7128,  lon: -74.0060 },
  { name: "Los Angeles",    lat: 34.0522,  lon: -118.2437 },
  { name: "Chicago",        lat: 41.8781,  lon: -87.6298 },
  { name: "Mumbai",         lat: 19.0760,  lon:  72.8777 },
  { name: "Bangalore",      lat: 12.9716,  lon:  77.5946 },
  { name: "Delhi",          lat: 28.7041,  lon:  77.1025 },
];

/**
 * Nearby businesses live directory driven by /api/food-businesses/near.
 * Renders results as a sortable list; the "map" is an OSM-based iframe
 * embed so no external map SDK / token is required.
 */
export default function CmsNearby() {
  const [lat, setLat] = useState("37.7749");
  const [lon, setLon] = useState("-122.4194");
  const [radius, setRadius] = useState("10");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/food-businesses/near", {
        params: {
          lat: parseFloat(lat), lon: parseFloat(lon),
          radius_miles: parseFloat(radius) || 10,
          limit: 60,
        },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nearby search failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Browser geolocation unavailable");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude.toFixed(6)));
        setLon(String(pos.coords.longitude.toFixed(6)));
        toast.success("Location set — press Search");
      },
      () => toast.error("Permission denied"),
    );
  };

  // OSM embed for a lightweight map preview (no API key required).
  const half = Math.max(0.05, parseFloat(radius || "5") / 40); // rough degrees
  const bbox = [
    (parseFloat(lon) - half).toFixed(4),
    (parseFloat(lat) - half).toFixed(4),
    (parseFloat(lon) + half).toFixed(4),
    (parseFloat(lat) + half).toFixed(4),
  ].join(",");
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;

  return (
    <div className="max-w-6xl" data-testid="cms-nearby">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Nearby Businesses</h1>
      <p className="mt-1 text-sm text-slate-500">Live map + directory backed by /api/food-businesses/near — the same feed /food-pulse/near-me consumes.</p>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div>
          <div className="text-[11px] text-slate-500">Latitude</div>
          <Input value={lat} onChange={(e) => setLat(e.target.value)} className="w-32" data-testid="cms-nearby-lat" />
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Longitude</div>
          <Input value={lon} onChange={(e) => setLon(e.target.value)} className="w-32" data-testid="cms-nearby-lon" />
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Radius (mi)</div>
          <Input value={radius} onChange={(e) => setRadius(e.target.value)} className="w-24" data-testid="cms-nearby-radius" />
        </div>
        <Button variant="outline" onClick={useMyLocation} className="h-9"><Locate className="mr-1 h-4 w-4" /> Use my location</Button>
        <Button onClick={load} disabled={loading} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cms-nearby-search">
          <Search className="mr-1 h-4 w-4" /> {loading ? "Searching…" : "Search"}
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            data-testid={`cms-nearby-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => { setLat(String(p.lat)); setLon(String(p.lon)); }}
            className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600 hover:border-emerald-300 hover:bg-emerald-50"
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <iframe
            title="Nearby businesses map"
            src={mapSrc}
            className="h-80 w-full"
            style={{ border: 0 }}
            data-testid="cms-nearby-map"
          />
          <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {items.map((b) => (
              <li key={b.slug || b.id} className="px-4 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1 text-sm font-medium text-slate-900">
                      <MapPin className="h-3 w-3 text-slate-400" /> {b.name}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {b.city || ""}{b.state ? `, ${b.state}` : ""} — {b.business_type || b.type || "—"}
                      {b.distance_miles != null && ` · ${b.distance_miles.toFixed(1)} mi`}
                    </div>
                  </div>
                  <Link to={`/food-pulse/businesses/${b.slug}`} target="_blank" className="shrink-0 text-xs text-emerald-700 hover:underline">
                    <ExternalLink className="inline h-3 w-3" /> View
                  </Link>
                </div>
              </li>
            ))}
            {items.length === 0 && !loading && (
              <li className="p-6 text-center text-sm text-slate-500">No businesses within {radius} mi. Try a wider radius or a different preset.</li>
            )}
          </ul>
        </div>
        <aside className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Summary</div>
          <div className="mt-2 text-sm text-slate-900">{items.length} businesses within {radius} miles</div>
          {items.length > 0 && (
            <div className="mt-3 space-y-1 text-xs text-slate-600">
              <div className="font-medium text-slate-700">Types</div>
              {Object.entries(items.reduce((acc, b) => {
                const t = b.business_type || b.type || "other";
                acc[t] = (acc[t] || 0) + 1;
                return acc;
              }, {})).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([t, n]) => (
                <div key={t} className="flex items-center justify-between">
                  <span>{t}</span><span className="tabular-nums text-slate-500">{n}</span>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
